/** @format */

import * as Primitive from '@radix-ui/react-radio-group'
import React, {ReactNode, useId} from 'react'

/**
 * RadioGroup component for rendering a group of radio buttons
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState('option1')
 * <RadioGroup
 *   label="Choose an option"
 *   value={value}
 *   onValueChange={setValue}
 *   options={[
 *     {value: 'option1', label: 'Option 1'},
 *     {value: 'option2', label: 'Option 2'},
 *     {value: 'option3', label: 'Option 3', disabled: true},
 *   ]}
 * />
 * ```
 */
type RadioGroupProps = {
  label?: ReactNode
  options: Array<{value: string; label: ReactNode; disabled?: boolean}>
  /**
   * Layout orientation
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical'
} & Omit<Primitive.RadioGroupProps, 'className'>

export function RadioGroup(props: RadioGroupProps) {
  const {label, options, orientation = 'vertical', ...radioGroupProps} = props
  return (
    <Primitive.RadioGroup {...radioGroupProps}>
      {label && <div className="mb-2 font-medium">{label}</div>}
      <div
        className={
          orientation === 'horizontal'
            ? 'flex flex-row gap-4'
            : 'flex flex-col gap-2'
        }
      >
        {options.map(option => (
          <RadioButtonField
            key={option.value}
            value={option.value}
            label={option.label}
            disabled={option.disabled}
          />
        ))}
      </div>
    </Primitive.RadioGroup>
  )
}

type RadioButtonFieldProps = {
  label: ReactNode
  value: string
} & Omit<Primitive.RadioGroupItemProps, 'className'>

export function RadioButtonField(props: RadioButtonFieldProps) {
  const genId = useId()
  const {id = genId, label, ...radioProps} = props
  return (
    <div className="flex items-start gap-2">
      <div className="inline-flex flex-none py-1">
        <RadioButton {...radioProps} id={id} />
      </div>
      <label htmlFor={id} className="cursor-pointer">
        {label}
      </label>
    </div>
  )
}

type RadioButtonProps = Omit<Primitive.RadioGroupItemProps, 'className'>

export function RadioButton(props: RadioButtonProps) {
  return (
    <Primitive.RadioGroupItem
      {...props}
      className="group inline-flex h-4 w-4 flex-none cursor-pointer rounded-full border border-gray-300 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:cursor-default disabled:border-gray-300 disabled:bg-gray-200 data-[state=checked]:border-primary data-[state=checked]:bg-white"
    >
      <Primitive.RadioGroupIndicator className="flex h-full w-full items-center justify-center">
        <div className="h-2 w-2 rounded-full bg-primary" />
      </Primitive.RadioGroupIndicator>
    </Primitive.RadioGroupItem>
  )
}
